import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import styled, { keyframes } from "styled-components";
import { useParams, useHistory, Link, useLocation } from "react-router-dom";
import { buildVendorParam, extractVendorId } from "../utils/vendorSlug";
import { productPathUnder } from "../utils/seoPaths";
import CrawlLink from "../components/CrawlLink";
import DOMPurify from "dompurify";
import { SEOHead, buildBrandSeoDescription } from "../components/SEO/SEOHead";
import { viewItem, vendorView } from "../utils/analytics";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import Layout from "../components/Global/Layout";
import {
  fetchBrandById,
  fetchLast5FeaturedImages,
  applyBrandSectionOrder,
  applyBranchProductChange,
  refreshSelectedBrand,
} from "../redux/brandSlice";
import useCatalogUpdates from "../hooks/useCatalogUpdates";
import { moveCartToOutlet, setCurrentBrand } from "../redux/brandCartSlice";
/* Branch operations (4 Oct 2026): each product as it stands at this branch,
   "Get it from" another, live branch changes and the cart check. */
import { branchProductChanged } from "../utils/branchCart";
import { addButtonLabel, blockedHere, branchClosedNotice } from "../utils/branchAvailability";
import BranchAvailabilityBadge from "../components/branch/BranchAvailabilityBadge";
import BranchCartReviewDialog from "../components/branch/BranchCartReviewDialog";
import { useCartBranchCheck } from "../hooks/useBranchCartCheck";
import useConfirmCartMove from "../hooks/useConfirmCartMove";
import OutletSwitchDialog from "../components/OutletSwitchDialog";
import {
  cartOutletId,
  findOutlet,
  outletIdFromSearch,
  outletLabel,
  sortLocationsForPicker,
  vendorLocationsOf,
  withOutletQuery,
} from "../utils/vendorOutlets";
import { openNowOf } from "../utils/vendorOpenState";
import LocationPicker, { hereNameOf, locationName } from "../components/LocationPicker";
import useDeliveryPoint from "../hooks/useDeliveryPoint";
import { calculateDistance } from "../redux/userLocationSlice";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import StorefrontIcon from "@mui/icons-material/Storefront";
import { Skeleton } from "@material-ui/lab";
import {
  Tooltip,
  Badge,
  IconButton,
  Modal,
  Backdrop,
  Fade,
} from "@mui/material";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AddShoppingCartIcon from "@mui/icons-material/AddShoppingCart";
import RequestQuoteIcon from "@mui/icons-material/RequestQuote";
import CatalogueSearch from "../components/shop/CatalogueSearch";
import { filterSectionsByQuery } from "../utils/menuSearch";
import ChatIcon from "@mui/icons-material/ChatBubbleOutline";
import useMarketPlacesAutocomplete from "../hooks/useMarketPlacesAutocomplete";
import {
  Combobox,
  ComboboxInput,
  ComboboxPopover,
  ComboboxList,
  ComboboxOption,
} from "@reach/combobox";
import "@reach/combobox/styles.css";
import placeholderImage from "../assets/img/menuImage.png";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.bubble.css";
import BrandCart from "../components/BrandCart";
import useCoverage from "../redux/useCoverage";
import useVendorAvailability from "../hooks/useRestaurantAvailability";
import CoverageBanner from "../components/CoverageBanner";
import useMediaQuery from "../hooks/useMediaQuery";
import RestaurantAvailabilityBanner from "../components/RestaurantAvailabilityBanner";
import OfficialStoreBadge from "../components/OfficialStoreBadge";
import VendorShareModal from "../components/Share/VendorShareModal";
import ShareIcon from "@mui/icons-material/Share";
import { buildVendorShareText, storefrontSearchTitle, withVendorCacheBuster } from "../components/SEO/vendorSeo";
import { getBrandTheme, getBrandThemeCssVars } from "../utils/brandTheme";
import LocationPrompt from "../components/LocationPrompt";
import Reviews from "../components/Reviews/Reviews";
import VendorWhatsAppFloat from "../components/VendorWhatsAppFloat";
import {
  getOriginalCloudinaryUrl,
  getResponsiveCloudinarySrcSet,
  optimizeCloudinaryUrl,
} from "../utils/cloudinaryOptimize";
import {
  CURRENCY,
  CURRENCY_LOCALE,
  DELIVERY_LOCATION_EXAMPLE,
  DIAL_CODE,
  SITE_ORIGIN,
  normalizeMarketLatLng,
} from "../config/market";
import { userRequest } from "../api/requestMethods";
import { geocodeAddress, latLngFromGeocode } from "../utils/googlePlaces";
import reportClientError from "../utils/clientTelemetry";
import DealTag from "../components/DealTag";
import PriceCut from "../components/PriceCut";
import { hasSalePrice } from "../utils/vendorDiscounts";
/* ONE rule for "is this sold by quote, and what does its price row say", shared
   with BrandProductPage, the healthcare storefront, search, the share text and
   both customer apps. This page used to read `product.priceHidden` directly in
   about a dozen places, which only ever worked on a payload that had been
   through the server's mapProductForPublic. */
import {
  PRICE_ON_REQUEST_LABEL,
  PRICE_UNAVAILABLE_LABEL,
  canRequestQuote,
  describeQuoteProgress,
  isQuoteOnly,
  publishedPrice,
} from "../utils/priceOnRequest";
/* The buyer's own quote requests, read the same way BrandProductPage reads
   them. This page is where the modal actually opens, so it is also where a
   second request for a product the buyer has already asked about has to be
   refused, and where the catalogue has to stop inviting one the moment it is
   sent. */
import useBuyerQuoteRequests, {
  duplicateQuoteRequestNotice,
} from "../hooks/useBuyerQuoteRequests";
import { vendorTaglineOf } from "../utils/vendorTagline";

const fmtUgx = (n) =>
  n == null || Number.isNaN(Number(n))
    ? "—"
    : new Intl.NumberFormat(CURRENCY_LOCALE, {
        style: "currency",
        currency: CURRENCY,
        maximumFractionDigits: 0,
      }).format(Number(n));

const HERO_IMAGE_WIDTHS = [600, 900, 1200, 1500];

/* Below this width the hero is barely 100px tall. A legacy card image or a
   16:9 artwork shown "contain" inside it becomes a small picture with a
   blurred letterbox either side, which reads as broken rather than branded,
   so phones only get slides authored at the hero's own 15:4 ratio. Matches
   the HeroShell breakpoint. */
const HERO_NARROW_QUERY = "(max-width: 768px)";

const buildHeroSlide = ({ id, url, alt, caption = "", isWide = false }) => {
  const originalUrl = getOriginalCloudinaryUrl(url);
  if (!originalUrl) return null;

  const imageOptions = {
    width: 1500,
    crop: "limit",
    quality: "auto:best",
    format: "auto",
  };

  return {
    id,
    originalUrl,
    src: optimizeCloudinaryUrl(originalUrl, imageOptions),
    srcSet: originalUrl.includes("res.cloudinary.com")
      ? getResponsiveCloudinarySrcSet(originalUrl, HERO_IMAGE_WIDTHS, {
          crop: "limit",
          quality: "auto:best",
          format: "auto",
        })
      : "",
    alt,
    caption,
    isWide,
  };
};

const BrandShopPage = () => {
  const { id: idParam } = useParams();
  const id = extractVendorId(idParam);
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  // The outlet (branch) this storefront is opened for, from `?outletId=`, the
  // same query the restaurant storefront reads. Null is the brand's main
  // store. Every outlet sells the full brand catalogue; what changes is whose
  // hours, coordinates and radius the checks below are made against, and
  // which outlet every cart line added here names.
  const outletId = outletIdFromSearch(location.search);
  const quoteAutoOpenRef = useRef(false);
  const { selectedBrand, isFetching, featuredImages, brandNotFound } = useSelector(
    (state) => state.brands,
  );
  const currentUser = useSelector((state) => state.user?.currentUser);
  const authToken = currentUser?.token || currentUser?.accessToken;
  const { carts } = useSelector((state) => state.brandCart);
  const cart = carts[id] || {};
  const cartCount = Object.values(cart).reduce(
    (total, item) => total + (Number(item.quantity) || 0),
    0,
  );
  // The outlet the lines already in this brand's cart were added from (null
  // for the main store). Choosing a different location with those lines in
  // the cart asks first and then moves them there, below.
  const cartOutlet = cartOutletId(cart);
  /* The cart check at its branch (4 Oct 2026): quiet while browsing (lines
     read back from localStorage or touched by a live branch change are
     settled or flagged without interrupting), and the "Changes at <branch>"
     review right after the cart moves to another branch. */
  const { reviewNextCheck, dialogProps: branchDialogProps } = useCartBranchCheck({
    vertical: "brand",
    cartId: id,
    checkWhen: Boolean(selectedBrand) && String(selectedBrand?._id) === String(id),
    silent: true,
  });
  // Pre-checkout coverage gate (backend /api/coverage — same decision as the
  // order-create gate) so the customer learns as early as possible (shop
  // entry / add-to-cart) instead of at the very last checkout step.
  // An outlet is checked against ITS coordinates and radius, not the brand's.
  const coverage = useCoverage({ vendorId: id, outletId, vendorType: "brand" });
  // Business hours. These verticals had no hours concept until the shared
  // availability service shipped; without this the vendor can mark themselves
  // closed in the dashboard and shoppers would never know. An outlet's hours
  // are its own, inheriting the brand's when it has published none.
  const availability = useVendorAvailability({
    vendorId: id,
    vendorType: "brand",
    outletId,
  });
  /* WHERE THIS BUYER'S REQUESTS HAVE GOT TO, for the whole catalogue in one
     read. Only worth asking for when something here is actually sold by quote,
     and the endpoint is behind `auth`, so a signed-out shopper and a brand that
     publishes every price both cost nothing. A failed read reads as "no
     request", which is the behaviour this page had before. */
  const brandSellsByQuote = useMemo(
    () =>
      (selectedBrand?.sections || []).some((section) =>
        (section?.products || []).some((product) => isQuoteOnly(product)),
      ),
    [selectedBrand],
  );
  const {
    requestFor: buyerQuoteRequestFor,
    refresh: refreshBuyerQuoteRequests,
  } = useBuyerQuoteRequests({ brandId: id, enabled: brandSellsByQuote });

  const [showLocationPrompt, setShowLocationPrompt] = useState(false);
  const [showCart, setShowCart] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [quoteProduct, setQuoteProduct] = useState(null);
  const [quoteSubmitting, setQuoteSubmitting] = useState(false);
  const [quoteForm, setQuoteForm] = useState({
    preferredChannel: "IN_PLATFORM",
    contactName: "",
    contactEmail: "",
    contactPhone: "",
    title: "",
    message: "",
    quantity: "",
    deliveryLocation: "",
    deadline: "",
  });
  const [deliveryLatLng, setDeliveryLatLng] = useState(null);
  const [deliveryAddressError, setDeliveryAddressError] = useState("");
  const [customFieldValues, setCustomFieldValues] = useState({});
  const [heroSlideIndex, setHeroSlideIndex] = useState(0);
  const [shareOpen, setShareOpen] = useState(false);
  const [heroPaused, setHeroPaused] = useState(false);
  const heroTouchStartXRef = useRef(null);

  const heroIsNarrow = useMediaQuery(HERO_NARROW_QUERY);

  const heroSlides = useMemo(() => {
    if (!selectedBrand || String(selectedBrand._id) !== String(id)) return [];

    const displayName =
      selectedBrand.tradingName || selectedBrand.name || "Brand";
    const candidates = [];
    const primaryBannerUrl =
      selectedBrand.shopBannerUrl || selectedBrand.bannerUrl;

    if (primaryBannerUrl) {
      candidates.push(
        buildHeroSlide({
          id: "shop-banner",
          url: primaryBannerUrl,
          alt: selectedBrand.heroAlt || `${displayName} shop banner`,
          isWide: Boolean(selectedBrand.shopBannerUrl),
        }),
      );
    }

    (featuredImages || []).forEach((artwork, index) => {
      const artworkBrandId = artwork?.brandId?._id || artwork?.brandId;
      if (artworkBrandId && String(artworkBrandId) !== String(id)) return;

      candidates.push(
        buildHeroSlide({
          id: artwork?._id || `featured-artwork-${index}`,
          url:
            artwork?.webImageUrl ||
            artwork?.mobileImageUrl ||
            artwork?.featuredImageUrl,
          alt:
            artwork?.caption || `${displayName} featured artwork ${index + 1}`,
          caption: artwork?.caption || "",
          // New editions carry a dedicated 15:4 web asset. Historical 16:9
          // editions retain the colour-matched contain fallback below.
          isWide: Boolean(artwork?.webImageUrl),
        }),
      );
    });

    const seen = new Set();
    const unique = candidates.filter((slide) => {
      if (!slide || seen.has(slide.originalUrl)) return false;
      seen.add(slide.originalUrl);
      return true;
    });

    // Only artwork authored at the hero's own 15:4 ratio fills the banner.
    // A logo card or 16:9 artwork sits letterboxed between blurred bars, so
    // it is left out on every screen size whenever full-width artwork
    // exists. A desktop brand with nothing wide keeps the letterboxed
    // artwork rather than an empty banner; phones fall back to the brand
    // colour panel because the hero is too short there to letterbox.
    const wide = unique.filter((slide) => slide.isWide);
    if (heroIsNarrow || wide.length) return wide;
    return unique;
  }, [featuredImages, heroIsNarrow, id, selectedBrand]);

  useEffect(() => {
    setHeroSlideIndex(0);
  }, [id, heroSlides.length]);

  useEffect(() => {
    if (heroSlides.length <= 1 || heroPaused) return undefined;
    if (
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches
    ) {
      return undefined;
    }

    const timer = window.setInterval(() => {
      setHeroSlideIndex((current) => (current + 1) % heroSlides.length);
    }, 6000);
    return () => window.clearInterval(timer);
  }, [heroPaused, heroSlides.length]);

  // Durable, URL-independent vendor attribution (GA4 vendor_view).
  const vendorViewedRef = useRef(null);
  useEffect(() => {
    const vid = selectedBrand?._id;
    if (!vid || vendorViewedRef.current === String(vid)) return;
    vendorViewedRef.current = String(vid);
    vendorView({ vendorId: vid, vendorType: "brand", path: location.pathname });
  }, [selectedBrand?._id, location.pathname]);

  const {
    value: deliveryAddressInput,
    setValue: setDeliveryAddressInput,
    suggestions: { data: deliverySuggestions },
    clearSuggestions: clearDeliverySuggestions,
    ready: placesReady,
    error: placesError,
  } = useMarketPlacesAutocomplete({ debounce: 300 });

  useEffect(() => {
    if (placesError) {
      setDeliveryAddressError(
        "Address search is temporarily unavailable. Please retry.",
      );
    }
  }, [placesError]);

  const handleDeliverySelect = async (address) => {
    setDeliveryAddressInput(address, false);
    clearDeliverySuggestions();
    setQuoteForm((f) => ({ ...f, deliveryLocation: address }));
    try {
      const results = await geocodeAddress(address);
      const raw = latLngFromGeocode(results[0]);
      const coordinates = normalizeMarketLatLng(raw.lat, raw.lng);
      if (!coordinates)
        throw new Error("The selected address is outside this delivery market");
      setDeliveryLatLng(coordinates);
      setDeliveryAddressError("");
    } catch (e) {
      reportClientError(e, { area: "brand_quote_geocode" });
      setDeliveryLatLng(null);
      setDeliveryAddressError(
        "We couldn’t pin that delivery location. Choose another suggestion.",
      );
    }
  };

  // Quote communication is deliberately platform-mediated. Keeping these
  // helpers makes the modal/login-resume flow stable while preventing a future
  // UI change from deriving direct channels from raw brand contact fields.
  const resolveDefaultChannel = useCallback(() => "IN_PLATFORM", []);
  const availableChannels = useCallback(() => ["IN_PLATFORM"], []);

  const openQuoteForProduct = useCallback(
    (product) => {
      if (!authToken) {
        // Persist the intent so it survives the /login round-trip (location
        // state is lost across the post-login redirect), then take the shopper
        // to sign-in. On return, the auto-open effect below reads this entry
        // and reopens the quote modal exactly where they left off — no
        // dead-end toast.
        try {
          sessionStorage.setItem(
            "pendingBrandQuote",
            JSON.stringify({
              brandId: id,
              product: product || null,
              ts: Date.now(),
            }),
          );
        } catch (_) {
          /* sessionStorage may be disabled */
        }
        toast.info("Sign in to request a quote — we'll bring you right back.");
        history.push({
          pathname: "/login",
          state: { from: { pathname: `/brand/shop/${idParam || id}` } },
        });
        return;
      }
      /* ONE live request per product. Two open requests give the vendor two
         things to answer and the buyer two places to look, so a shopper who has
         already asked is pointed at the conversation they already have instead
         of quietly starting a second one. The brand-level "Request quote"
         button passes no product and is never caught by this: it is a question
         about the brand, not about a SKU. */
      const existingRequest = buyerQuoteRequestFor(product?._id);
      if (product && !canRequestQuote(existingRequest)) {
        toast.info(
          duplicateQuoteRequestNotice({
            productName: product.name,
            vendorName: selectedBrand?.tradingName || selectedBrand?.name,
          }),
          { autoClose: 8000 },
        );
        return;
      }

      setQuoteProduct(product || null);
      const buyerNameSeed =
        currentUser?.name ||
        currentUser?.businessName ||
        currentUser?.result?.name ||
        "";
      const buyerEmailSeed =
        currentUser?.email || currentUser?.result?.email || "";
      const buyerPhoneSeed =
        currentUser?.phone || currentUser?.result?.phone || "";
      setQuoteForm({
        preferredChannel: resolveDefaultChannel(selectedBrand),
        contactName: buyerNameSeed,
        contactEmail: buyerEmailSeed,
        contactPhone: buyerPhoneSeed,
        title: product ? `Quote request — ${product.name}` : "",
        message: "",
        quantity: "",
        deliveryLocation: "",
        deadline: "",
      });
      setDeliveryAddressInput("", false);
      clearDeliverySuggestions();
      setDeliveryLatLng(null);
      setCustomFieldValues({});
      setShowQuoteModal(true);
    },
    [
      authToken,
      buyerQuoteRequestFor,
      clearDeliverySuggestions,
      currentUser,
      history,
      id,
      idParam,
      resolveDefaultChannel,
      selectedBrand,
      setDeliveryAddressInput,
    ],
  );

  // Safety net: if the modal is open and the currently selected channel isn't
  // among those the brand actually supports (e.g. brand was edited mid-flight,
  // or initial render bled through before openQuoteForProduct seeded the
  // default), snap to the first available channel. This prevents submitting
  // an invalid channel and the resulting backend rejection.
  useEffect(() => {
    if (!showQuoteModal || !selectedBrand) return;
    const allowed = availableChannels(selectedBrand);
    if (!allowed.includes(quoteForm.preferredChannel)) {
      setQuoteForm((f) => ({ ...f, preferredChannel: allowed[0] }));
    }
  }, [
    showQuoteModal,
    selectedBrand,
    quoteForm.preferredChannel,
    availableChannels,
  ]);

  /* The brand, read at the branch this storefront is open on (4 Oct 2026),
     so every product shows as it stands there. Choosing another branch of the
     same brand reads it again QUIETLY (no skeleton, the page stays put); a
     new brand or a sign-in is a full load as before. */
  const outletIdRef = useRef(outletId);
  outletIdRef.current = outletId;
  useEffect(() => {
    dispatch(fetchBrandById({ brandId: id, outletId: outletIdRef.current }));
    dispatch(fetchLast5FeaturedImages(id));
    dispatch(setCurrentBrand(id));
  }, [dispatch, id, authToken]);
  const branchReadRef = useRef({ id, outletId });
  const loadedBrandIdRef = useRef(null);
  loadedBrandIdRef.current = selectedBrand?._id ? String(selectedBrand._id) : null;
  useEffect(() => {
    const last = branchReadRef.current;
    branchReadRef.current = { id, outletId };
    if (last.id !== id || (last.outletId || null) === (outletId || null)) return;
    // Still loading the first read (at the old branch): read again in full,
    // so the page never settles on the branch the shopper just left.
    dispatch(
      loadedBrandIdRef.current === String(id)
        ? refreshSelectedBrand({ brandId: id, outletId })
        : fetchBrandById({ brandId: id, outletId }),
    );
  }, [dispatch, id, outletId]);

  // Live storefront, the same catalog:public feed VendorShopPage rides:
  //  - `section:orderChanged` (an operator dragged products into a new order
  //    on the dashboard) is applied IN PLACE from the payload — no refetch;
  //  - `product:statusChanged` for this brand (an item activated or
  //    deactivated) re-pulls the brand, since the server stays the source of
  //    truth for what a given viewer may see.
  const brandOwnerRef = useRef(null);
  useEffect(() => {
    brandOwnerRef.current = selectedBrand?.owner
      ? String(selectedBrand.owner)
      : null;
  }, [selectedBrand?.owner]);

  /* Hours edited on the dashboard reach this storefront live, the way
     RestaurantShopPage's `restaurantHoursUpdated` handler does: the payload
     names the brand or the outlet whose hours changed, and this storefront
     re-reads its availability when that is the brand it shows, the outlet it
     shows, or the parent of the outlet it shows (an outlet inheriting the
     brand's hours changes with them). Held in a ref because
     `availability.refresh` changes identity between renders; in the socket
     effect's deps it would rebuild the websocket on every render. */
  const hoursChangedRef = useRef(() => {});
  useEffect(() => {
    hoursChangedRef.current = (payload = {}) => {
      const changed = String(payload.brandId || payload.outletId || "");
      if (!changed) return;
      const parent = String(payload.parentBrandId || "");
      const mine =
        changed === String(id) ||
        changed === String(outletId || "") ||
        parent === String(id);
      if (mine) availability.refresh({ silent: true });
    };
  });

  useCatalogUpdates(
    {
      brandHoursUpdated: (payload) => {
        hoursChangedRef.current(payload);
      },
      "section:orderChanged": (payload) => {
        if (payload.vendorType && payload.vendorType !== "brand") return;
        if (String(payload.vendorId || "") !== String(id)) return;
        dispatch(
          applyBrandSectionOrder({
            brandId: id,
            sectionId: payload.sectionId,
            productIds: payload.productIds,
          }),
        );
      },
      // A branch of this brand was switched on or off. Refresh quietly, so
      // the location picker (open or not) drops or regains it while the
      // shopper is on the page (4 Oct 2026).
      outletStatusChanged: (payload) => {
        if (payload.parentType !== "brand") return;
        if (String(payload.brandId || "") !== String(id)) return;
        dispatch(refreshSelectedBrand({ brandId: id, outletId }));
      },
      "product:statusChanged": (payload) => {
        const mine =
          String(payload.vendorId || "") === String(id) ||
          (brandOwnerRef.current &&
            String(payload.ownerId || "") === brandOwnerRef.current);
        if (mine) dispatch(fetchBrandById({ brandId: id, outletId }));
      },
      /* A branch of a brand switched a product on or off, or sold it out
         (4 Oct 2026). The open storefront flips the badge in place (the
         slice ignores other brands), and any cart line it touches is checked
         again before the shopper pays. */
      "branchProduct:changed": (payload) => {
        dispatch(branchProductChanged(payload));
        if (payload.vertical && payload.vertical !== "brand") return;
        dispatch(applyBranchProductChange(payload));
      },
    },
    { enabled: Boolean(id) },
  );

  useEffect(() => {
    const name = selectedBrand?.tradingName || selectedBrand?.name;
    if (!name) return;
    const canonical = buildVendorParam(name, id);
    if (idParam !== canonical) {
      // Keep the outlet: the slug redirect used to drop the query string.
      history.replace(withOutletQuery(`/brand/shop/${canonical}`, outletId));
    }
  }, [
    selectedBrand?.tradingName,
    selectedBrand?.name,
    id,
    idParam,
    outletId,
    history,
  ]);

  // The outlet named in the URL, resolved against the loaded brand (active
  // outlets only). One that is gone is handled below `chooseLocation`.
  const selectedOutlet = findOutlet(selectedBrand, outletId);

  /**
   * Every place this brand can be ordered from (its main branch and each
   * active outlet, each with its distance from the shopper), for the
   * location picker under the identity card. The Brands page opened this
   * storefront on the best of them for the shopper; the picker shows which
   * and lets them choose another (4 Oct 2026). It replaces the row of outlet
   * chips that sat under the brand's name.
   */
  const deliveryPoint = useDeliveryPoint();
  const brandLocations = useMemo(
    () =>
      selectedBrand && String(selectedBrand._id) === String(id)
        ? vendorLocationsOf(selectedBrand, { deliveryPoint, calculateDistance })
        : [],
    [selectedBrand, id, deliveryPoint],
  );

  /**
   * Open this brand's storefront on another of its locations (an outlet, or
   * the main store), keeping the slug the page has already canonicalised to.
   *
   * The cart follows the shopper. It is fulfilled from ONE location (every
   * line carries the same outlet id and checkout sends it as the order's
   * fulfilmentOutletId), so the lines move to the new location instead of
   * being cleared. A branch can have its own sold out, on/off and price
   * (4 Oct 2026), so every moved line is checked there and "Changes at
   * <branch>" shows what differs before the shopper pays. The picker asks
   * first only when the cart holds lines bound to a different location of
   * this brand (`cartBoundElsewhere`), as the apps do, and so does "Get it
   * from" on a product card (`getItFrom`, shopper journey audit 4 Oct 2026):
   * a tap for one product must not move a whole basket without a word.
   */
  const outletPath = useCallback(
    (targetOutletId) =>
      withOutletQuery(location.pathname, targetOutletId || null),
    [location.pathname],
  );
  const cartBoundElsewhere = useCallback(
    (picked) =>
      cartCount > 0 &&
      (cartOutlet || null) !== (picked?.isOutlet ? String(picked._id) : null),
    [cartCount, cartOutlet],
  );
  const chooseLocation = useCallback(
    (picked) => {
      const target = picked?.isOutlet ? String(picked._id) : null;
      if (cartCount > 0) {
        dispatch(moveCartToOutlet({ brandId: id, outletId: target }));
        reviewNextCheck();
      }
      history.push(outletPath(target));
    },
    [cartCount, dispatch, history, id, outletPath, reviewNextCheck],
  );
  const getItFrom = useConfirmCartMove({
    needsAsk: cartBoundElsewhere,
    onMove: chooseLocation,
  });

  /* The outlet named in the URL is gone: switched off while the page was open
     (the quiet refresh above drops it), deleted, or named by a stale link. It
     cannot fulfil an order and the server would refuse one that named it, so
     the storefront moves to the best location left, cart and all, and says
     why. Same as the apps (4 Oct 2026); it used to fall back to the main store
     and leave the cart bound to the dead outlet. */
  useEffect(() => {
    if (!outletId || !selectedBrand) return;
    if (String(selectedBrand._id) !== String(id)) return;
    if (findOutlet(selectedBrand, outletId)) return;
    const best = sortLocationsForPicker(brandLocations, { isOpen: openNowOf })[0];
    const target = best?.isOutlet ? String(best._id) : null;
    const outlets = Array.isArray(selectedBrand.outlets) ? selectedBrand.outlets : [];
    const closedName = outlets.find((outlet) => String(outlet?._id) === String(outletId))?.name;
    const brandName = selectedBrand.tradingName || selectedBrand.name || "";
    const nextName = best ? locationName(best, brandName) : "the main store";
    if (cartCount > 0) {
      dispatch(moveCartToOutlet({ brandId: id, outletId: target }));
      reviewNextCheck();
    }
    const notice = branchClosedNotice({
      closed: closedName,
      next: nextName,
      cartMoved: cartCount > 0,
    });
    toast.info(`${notice.title}. ${notice.message}`);
    history.replace(outletPath(target));
  }, [outletId, selectedBrand, id, brandLocations, cartCount, dispatch, history, outletPath, reviewNextCheck]);

  useEffect(() => {
    if (quoteAutoOpenRef.current || !selectedBrand) return;
    if (String(selectedBrand._id) !== String(id)) return;

    // Primary path: in-memory state from same-tab navigation.
    let quoteProduct = location.state?.openQuoteProduct || null;
    let hasPendingQuote = Boolean(quoteProduct);

    // Fallback: sessionStorage entry left by this page or BrandProductPage
    // before a /login round-trip (which strips location.state). Only consume
    // it when it matches the current brand and is recent (< 30 min). A null
    // product is valid — it's the brand-level "Request quote" header button.
    if (!hasPendingQuote && authToken) {
      try {
        const raw = sessionStorage.getItem("pendingBrandQuote");
        if (raw) {
          const parsed = JSON.parse(raw);
          const fresh = parsed?.ts && Date.now() - parsed.ts < 30 * 60 * 1000;
          if (fresh && String(parsed.brandId) === String(id)) {
            hasPendingQuote = true;
            quoteProduct = parsed.product || null;
          }
        }
      } catch (_) {
        /* ignore parse / storage errors */
      }
    }

    if (!hasPendingQuote) return;

    // When signed out, openQuoteForProduct persists the intent and routes to
    // /login itself — either way the shopper ends up in the quote modal.
    quoteAutoOpenRef.current = true;
    sessionStorage.removeItem("pendingBrandQuote");
    openQuoteForProduct(quoteProduct);
    if (location.state?.openQuoteProduct) {
      history.replace({
        pathname: location.pathname,
        search: location.search,
        state: {},
      });
    }
  }, [
    authToken,
    history,
    id,
    location.pathname,
    location.search,
    location.state,
    openQuoteForProduct,
    selectedBrand,
  ]);

  const submitQuoteRequest = async (e) => {
    e.preventDefault();
    if (!authToken) {
      toast.error("Please sign in to request a quote.");
      return;
    }
    setQuoteSubmitting(true);
    try {
      const specs = [
        quoteProduct?.skuCode ? `SKU: ${quoteProduct.skuCode}` : null,
        `Quantity needed: ${quoteForm.quantity}`,
        `Delivery location: ${quoteForm.deliveryLocation}`,
        deliveryLatLng
          ? `Coordinates: ${deliveryLatLng.lat.toFixed(6)}, ${deliveryLatLng.lng.toFixed(6)}`
          : null,
      ]
        .filter(Boolean)
        .join("\n");
      const productSnapshot = quoteProduct
        ? {
            name: quoteProduct.name,
            skuCode: quoteProduct.skuCode,
            image: quoteProduct.image || quoteProduct.imageUrl,
          }
        : undefined;

      await userRequest.post(
        `/api/brands/${id}/quote-requests`,
        {
          title: quoteForm.title,
          message: quoteForm.message,
          specs,
          desiredDeadline: quoteForm.deadline
            ? new Date(quoteForm.deadline).toISOString()
            : undefined,
          deliveryLatLng,
          customFieldValues,
          preferredChannel: quoteForm.preferredChannel,
          contactSnapshot: {
            name: quoteForm.contactName,
            email: quoteForm.contactEmail,
            phone: quoteForm.contactPhone,
          },
          productId: quoteProduct?._id,
          productSnapshot,
        },
        { headers: { Authorization: `Bearer ${authToken}` } },
      );

      /* The old toast named My Quotes but gave no way to reach it, so the one
         next step it mentioned was the one the shopper could not take. The
         wording is unchanged; it is now a link, and the toast is held open long
         enough to be clicked. */
      toast.success(
        <span>
          Quote request sent.{" "}
          <Link
            to="/my_profile/my_quotes"
            style={{ color: "inherit", fontWeight: 700, textDecoration: "underline" }}
          >
            Track progress in My Quotes
          </Link>
          .
        </span>,
        { autoClose: 8000 },
      );
      setShowQuoteModal(false);
      setQuoteProduct(null);
      setQuoteForm({
        preferredChannel: "IN_PLATFORM",
        contactName: "",
        contactEmail: "",
        contactPhone: "",
        title: "",
        message: "",
        quantity: "",
        deliveryLocation: "",
        deadline: "",
      });
      setDeliveryAddressInput("", false);
      clearDeliverySuggestions();
      setDeliveryLatLng(null);
      setCustomFieldValues({});
      /* Re-read the buyer's requests so the catalogue behind the modal catches
         up with what they have just done. Without this a shopper who stays on
         the page is still invited to request a quote they sent seconds ago, and
         the card goes on saying nothing about it until a reload. */
      refreshBuyerQuoteRequests();
    } catch (err) {
      toast.error(
        err.response?.data?.message || "Could not send quote request.",
      );
    } finally {
      setQuoteSubmitting(false);
    }
  };

  // This storefront's canonical address. Product links and the product page
  // a tap opens both sit under it, so the two always agree.
  const storefrontPath = `/brand/shop/${buildVendorParam(selectedBrand?.tradingName || selectedBrand?.name, id)}`;
  const productLink = (product) => ({
    pathname: productPathUnder(storefrontPath, product),
    search: withOutletQuery("", outletId),
  });

  const handleProductClick = (product) => {
    if (coverage.isOutOfRange) {
      toast.warn(
        "This vendor doesn't deliver to your set location. Change your location or pick a vendor that delivers to you.",
      );
      return;
    }
    viewItem({
      id: product._id,
      name: product.name,
      price: product.price,
      vendorName: selectedBrand?.name,
    });
    // The shareable path, with both ids: what lands in the address bar is
    // what people copy, and /brand/product/:id alone cannot be opened cold.
    // The outlet rides along in the query so the product page stamps it on
    // the cart line (and links back to this outlet's storefront).
    history.push({
      ...productLink(product),
      state: { product, brand: selectedBrand, brandId: id, outletId },
    });
  };

  const handleCartClick = () => {
    // Route to the unified Cart Hub instead of the per-brand floating drawer,
    // so there is a single cart surface across all vendor types.
    history.push("/cart");
    return;
    // eslint-disable-next-line no-unreachable
    if (cartCount > 0) {
      setShowCart(true);
    } else {
      // If cart is empty, still show the cart modal
      setShowCart(true);
    }
  };

  if (isFetching) {
    return (
      <Layout>
        <Container>
          <Skeleton variant="rect" width="100%" height={400} />
          <Skeleton
            variant="text"
            width="60%"
            height={50}
            style={{ marginTop: 20 }}
          />
          <Skeleton variant="text" width="40%" height={30} />
        </Container>
      </Layout>
    );
  }

  if (!selectedBrand) {
    /* Two different states. The brand does not exist (the API said 404):
       say so, and tell search engines not to index the address. The read
       failed or never answered: the brand may well exist, so the page keeps
       the address it was opened at (the named one every link and the
       sitemap use), stays indexable, and offers a retry. Marking this state
       noindex, as it was until 18 Oct 2026, dropped a live storefront out of
       Google whenever the API blinked during Google's render. */
    const retry = () => dispatch(fetchBrandById({ brandId: id, outletId: outletIdRef.current }));
    return (
      <Layout>
        {brandNotFound ? (
          <SEOHead
            title="Brand not found"
            description="This brand page is missing or the link may be incorrect."
            canonicalPath={storefrontPath}
            noindex
          />
        ) : (
          <SEOHead
            title="Store"
            description="This store could not be loaded right now."
            canonicalPath={location.pathname}
          />
        )}
        <Container>
          {brandNotFound ? (
            <ErrorMessage>Brand not found</ErrorMessage>
          ) : (
            <ErrorMessage>
              This store could not be loaded. Check your connection and try again.
              <div>
                <RetryButton type="button" onClick={retry}>
                  Try again
                </RetryButton>
              </div>
            </ErrorMessage>
          )}
        </Container>
      </Layout>
    );
  }

  const hasProducts =
    selectedBrand.sections &&
    selectedBrand.sections.some(
      (section) => section.products && section.products.length > 0,
    );
  // Live products narrowed by the search, filtered ONCE (utils/menuSearch.js)
  // instead of the same predicate in the section filter, the product filter
  // and the "no results" test. Not a hook: this sits below the early returns.
  const visibleSections = filterSectionsByQuery(selectedBrand.sections, searchQuery);

  const catalogueLocked = !!selectedBrand.catalogueLocked;
  const catalogueLockReason = selectedBrand.catalogueLockReason;
  const brandTheme = getBrandTheme(selectedBrand);
  const { primary, secondary, onPrimary, onSecondary } = brandTheme;
  // The brand's tagline: one factual line the brand or Lusuku staff wrote
  // (utils/vendorTagline.js, shared with every vertical). A short value
  // ("Packaging", "Coffee roasters") is a label and reads best as a pill; a
  // long one is a sentence and has to stay prose, or the pill wraps into a
  // green blob.
  const taglineText = vendorTaglineOf(selectedBrand) || "";
  const specialityAsPill =
    taglineText.length > 0 &&
    taglineText.length <= 34 &&
    taglineText.split(/\s+/).length <= 4 &&
    !/[.!?]$/.test(taglineText);
  const displayTitle = selectedBrand.tradingName || selectedBrand.name;
  // The branch the products below were read at, named as the apps name it:
  // "<brand> (main branch)" for the main store of a brand with branches, the
  // brand's own name for a one-shop brand.
  const hereName = hereNameOf(brandLocations, selectedOutlet, displayTitle);
  const initials = (displayTitle || "B")
    .split(/\s+/)
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  const seoTitle = displayTitle || selectedBrand.name || "Brand shop";
  const seoDescription = buildBrandSeoDescription(selectedBrand);
  const seoImage =
    selectedBrand.shopBannerUrl ||
    selectedBrand.bannerUrl ||
    selectedBrand.imageUrl ||
    selectedBrand.logoUrl;

  // Share link: the canonical slug URL (so a renamed brand still resolves),
  // cache-busted with the brand's updatedAt so WhatsApp re-scrapes the OG card
  // after a banner change. Up to five enabled products ride along in the
  // message text — the same shape the restaurant and vendor pages share.
  const shareUrl = withVendorCacheBuster(
    `${SITE_ORIGIN}/brand/shop/${buildVendorParam(displayTitle, id)}`,
    selectedBrand,
  );
  const shareProducts = (selectedBrand.sections || [])
    .flatMap((section) => section.products || [])
    .filter((p) => p && p.enabled !== false)
    .slice(0, 5);
  const shareText = buildVendorShareText(selectedBrand, {
    url: shareUrl,
    products: shareProducts,
    headerLabel: "Official Store",
  });

  const showPreviousHeroSlide = () => {
    setHeroSlideIndex(
      (current) =>
        (current - 1 + heroSlides.length) % Math.max(heroSlides.length, 1),
    );
  };

  const showNextHeroSlide = () => {
    setHeroSlideIndex(
      (current) => (current + 1) % Math.max(heroSlides.length, 1),
    );
  };

  const handleHeroKeyDown = (event) => {
    if (heroSlides.length <= 1) return;
    if (event.key === "ArrowLeft") {
      event.preventDefault();
      showPreviousHeroSlide();
    } else if (event.key === "ArrowRight") {
      event.preventDefault();
      showNextHeroSlide();
    }
  };

  const handleHeroTouchStart = (event) => {
    heroTouchStartXRef.current = event.touches?.[0]?.clientX ?? null;
  };

  const handleHeroTouchEnd = (event) => {
    const startX = heroTouchStartXRef.current;
    const endX = event.changedTouches?.[0]?.clientX;
    heroTouchStartXRef.current = null;
    if (startX == null || endX == null || heroSlides.length <= 1) return;
    const movement = endX - startX;
    if (Math.abs(movement) < 45) return;
    if (movement > 0) showPreviousHeroSlide();
    else showNextHeroSlide();
  };

  return (
    <Layout>
      <SEOHead
        title={seoTitle}
        searchTitle={storefrontSearchTitle("brand", displayTitle)}
        description={seoDescription}
        canonicalPath={storefrontPath}
        imageUrl={seoImage}
        type="website"
        brand={selectedBrand}
      />
      <Container style={getBrandThemeCssVars(selectedBrand)}>
        {/* ── Cinematic banner ── */}
        <HeroWrap>
          <BackButton type="button" onClick={() => history.goBack()}>
            ← Back
          </BackButton>
          <HeroShell
            aria-label={`${displayTitle} artwork`}
            aria-roledescription="carousel"
            tabIndex={heroSlides.length > 1 ? 0 : -1}
            onKeyDown={handleHeroKeyDown}
            onMouseEnter={() => setHeroPaused(true)}
            onMouseLeave={() => setHeroPaused(false)}
            onFocusCapture={() => setHeroPaused(true)}
            onBlurCapture={() => setHeroPaused(false)}
            onTouchStart={handleHeroTouchStart}
            onTouchEnd={handleHeroTouchEnd}
          >
            {heroSlides.length ? (
              <>
                <HeroTrack $index={heroSlideIndex}>
                  {heroSlides.map((slide, index) => (
                    <HeroSlide
                      key={slide.id}
                      aria-hidden={index !== heroSlideIndex}
                    >
                      {!slide.isWide && (
                        <HeroArtworkBackdrop
                          aria-hidden="true"
                          style={{ backgroundImage: `url("${slide.src}")` }}
                        />
                      )}
                      <HeroBannerImg
                        src={slide.src}
                        srcSet={slide.srcSet || undefined}
                        sizes="(max-width: 1240px) calc(100vw - 40px), 1200px"
                        alt={slide.alt}
                        loading={index === 0 ? "eager" : "lazy"}
                        decoding="async"
                        fetchpriority={index === 0 ? "high" : "auto"}
                        $fit={slide.isWide}
                      />
                    </HeroSlide>
                  ))}
                </HeroTrack>

                {heroSlides.length > 1 && (
                  <>
                    <HeroArrow
                      type="button"
                      $side="left"
                      onClick={showPreviousHeroSlide}
                      aria-label="Show previous artwork"
                    >
                      ‹
                    </HeroArrow>
                    <HeroArrow
                      type="button"
                      $side="right"
                      onClick={showNextHeroSlide}
                      aria-label="Show next artwork"
                    >
                      ›
                    </HeroArrow>
                    <HeroDots aria-label="Choose artwork">
                      {heroSlides.map((slide, index) => (
                        <HeroDot
                          key={slide.id}
                          type="button"
                          $active={index === heroSlideIndex}
                          onClick={() => setHeroSlideIndex(index)}
                          aria-label={`Show artwork ${index + 1} of ${heroSlides.length}`}
                          aria-current={
                            index === heroSlideIndex ? "true" : undefined
                          }
                        />
                      ))}
                    </HeroDots>
                  </>
                )}
              </>
            ) : (
              <HeroBannerFallback $primary={primary} />
            )}
          </HeroShell>
        </HeroWrap>

        {/* ── Brand identity card ── */}
        <BrandIdentityCard>
          <BrandIdentityLeft>
            {selectedBrand.imageUrl ? (
              <BrandLogoImg
                src={selectedBrand.imageUrl}
                alt={displayTitle}
                onError={(e) => {
                  e.target.style.display = "none";
                }}
              />
            ) : (
              <BrandLogoInitials $primary={primary}>
                {initials}
              </BrandLogoInitials>
            )}
            <BrandIdentityText>
              <BrandIdentityTitle>{displayTitle}</BrandIdentityTitle>
              {selectedBrand.legalName &&
                selectedBrand.legalName !== displayTitle && (
                  <BrandIdentityLegal>
                    {selectedBrand.legalName}
                  </BrandIdentityLegal>
                )}
              {selectedOutlet ? (
                <BrandIdentityMeta>
                  <StorefrontIcon style={{ fontSize: 15 }} />
                  <span>{outletLabel(selectedOutlet)}</span>
                </BrandIdentityMeta>
              ) : (
                selectedBrand.address && (
                  <BrandIdentityMeta>
                    <LocationOnIcon style={{ fontSize: 15 }} />
                    <span>{selectedBrand.address}</span>
                  </BrandIdentityMeta>
                )
              )}
              {taglineText ? (
                specialityAsPill ? (
                  <SpecialityPill $bg={primary} $fg={onPrimary}>
                    {taglineText}
                  </SpecialityPill>
                ) : (
                  <BrandIdentityTagline>{taglineText}</BrandIdentityTagline>
                )
              ) : null}
            </BrandIdentityText>
          </BrandIdentityLeft>
          <BrandIdentityRight>
            <TrustStrip>
              {selectedBrand.verifiedVendorBadge !== false && (
                <OfficialStoreBadge size="small" />
              )}
              {selectedBrand.fulfilment?.deliveryCoverage && (
                <TrustChip>
                  {String(selectedBrand.fulfilment.deliveryCoverage).replace(
                    /_/g,
                    " ",
                  )}
                </TrustChip>
              )}
              {selectedBrand.fulfilment?.minimumOrderValueUgx ? (
                <TrustChip>
                  Min. order{" "}
                  {fmtUgx(selectedBrand.fulfilment.minimumOrderValueUgx)}
                </TrustChip>
              ) : null}
              {selectedBrand.fulfilment?.leadTimeStandardDays != null ? (
                <TrustChip>
                  Ships in ~ {selectedBrand.fulfilment.leadTimeStandardDays} day
                  {selectedBrand.fulfilment.leadTimeStandardDays !== 1
                    ? "s"
                    : ""}
                </TrustChip>
              ) : null}
            </TrustStrip>
            <CtaRow>
              {selectedBrand.flows?.customQuote && (
                <HeroButton
                  type="button"
                  onClick={() => openQuoteForProduct(null)}
                >
                  Request quote
                </HeroButton>
              )}
              <ShareStoreButton
                type="button"
                onClick={() => setShareOpen(true)}
                aria-label={`Share ${displayTitle}`}
                title="Share this store"
              >
                <ShareIcon style={{ fontSize: 18 }} />
                Share
              </ShareStoreButton>
            </CtaRow>
          </BrandIdentityRight>
        </BrandIdentityCard>

        {/* Which of the brand's locations this storefront orders from, and
            the dialog to choose another. Nothing for a one-location brand. */}
        <LocationPicker
          locations={brandLocations}
          currentId={selectedOutlet ? selectedOutlet._id : selectedBrand._id}
          vendorName={displayTitle}
          onChoose={chooseLocation}
          deliveryPoint={deliveryPoint}
          confirmChoice={cartBoundElsewhere}
          accent={primary}
          onAccent={onPrimary}
          maxWidth="1200px"
        />

        <VendorShareModal
          open={shareOpen}
          onClose={() => setShareOpen(false)}
          url={shareUrl}
          title={displayTitle}
          description={seoDescription}
          imageUrl={seoImage}
          shareText={shareText}
        />

        <RestaurantAvailabilityBanner availability={availability} />

        <CoverageBanner
          variant="block"
          deliverable={coverage.deliverable}
          reason={coverage.reason}
          distanceKm={coverage.distanceKm}
          radius={coverage.radius}
          coverageCountry={coverage.coverageCountry}
          hasLocation={coverage.hasLocation}
          status={coverage.status}
          error={coverage.error}
          onRetry={coverage.refresh}
          onChangeLocation={() => setShowLocationPrompt(true)}
        />

        {selectedBrand.description && (
          <BrandDescriptionBar
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(selectedBrand.description),
            }}
          />
        )}

        {catalogueLocked ? (
          <LockCard>
            <LockIcon>🔒</LockIcon>
            <LockTitle>Sign in to view this catalogue</LockTitle>
            <LockText>
              {catalogueLockReason === "CATALOGUE_RESTRICTED"
                ? "This brand's catalogue is restricted to approved accounts. Contact support or the brand if you believe you should have access."
                : "Product catalogues on Lusuku are shown to signed-in accounts only — so brands always know who's browsing. Create an account or log in to see products and prices."}
            </LockText>
            {!authToken ? (
              <Link to="/login">
                <BrandPrimaryButton type="button">
                  Sign in to browse
                </BrandPrimaryButton>
              </Link>
            ) : null}
          </LockCard>
        ) : null}

        {!catalogueLocked && hasProducts ? (
          <>
            <CatalogueSearch
              value={searchQuery}
              onChange={setSearchQuery}
              placeholder="Search products in this catalogue…"
              ariaLabel="Search this catalogue"
            />
            <SectionsContainer>
              {visibleSections
                .map((section) => (
                  <Section key={section._id} id={`section-${section._id}`}>
                    <SectionTitle>{section.title}</SectionTitle>
                    {section.description && (
                      <SectionDescription>
                        {section.description}
                      </SectionDescription>
                    )}
                    <ProductsGrid>
                      {section.products
                        .map((product) => {
                          const cartItem = Object.values(cart).filter((line) => line._id === product._id).reduce((summary, line) => ({ quantity: summary.quantity + (Number(line.quantity) || 0) }), { quantity: 0 });
                          const isInCart = Boolean(
                            cartItem && (Number(cartItem.quantity) || 0) > 0,
                          );
                          const cartQuantity = cartItem
                            ? Number(cartItem.quantity) || 0
                            : 0;
                          /* The posture, once per card, so the badge, the
                             price row and the button cannot disagree with each
                             other the way they used to. */
                          const quoteOnly = isQuoteOnly(product);
                          const shownPrice = publishedPrice(product);
                          const unpriced = !quoteOnly && shownPrice === null;
                          const onOffer = !quoteOnly && hasSalePrice(product);
                          /* Sold out or switched off at this branch. The card
                             says so and offers the nearest branch that has
                             it; a quote stays available either way. */
                          const branchBlocked = !quoteOnly && blockedHere(product);
                          /* This buyer's own progress on this SKU, so a card
                             they have already asked about stops looking like
                             one they have not. Null for a product they have
                             never asked about, which is every card for a
                             signed-out shopper. */
                          const quoteProgress = quoteOnly
                            ? describeQuoteProgress(
                                buyerQuoteRequestFor(product._id),
                                selectedBrand?.tradingName ||
                                  selectedBrand?.name,
                              )
                            : null;
                          return (
                            <ProductCard
                              key={product._id}
                              onClick={() => handleProductClick(product)}
                            >
                              <ProductImageWrap>
                                <CrawlLink
                                  to={productLink(product)}
                                  onNavigate={() => handleProductClick(product)}
                                  style={{ display: "block", height: "100%" }}
                                >
                                  <ProductImage
                                    src={product.image || placeholderImage}
                                    alt={product.name}
                                    onError={(e) => {
                                      e.target.src = placeholderImage;
                                    }}
                                  />
                                </CrawlLink>
                                {onOffer && (
                                  <DealTag
                                    discount={product.discount}
                                    size="m"
                                    radius={14}
                                  />
                                )}
                                {/* Same INHERIT trap as the quote badge below:
                                    the override alone misses a brand whose whole
                                    catalogue is custom print. Reads the derived
                                    flow first and falls back to the override,
                                    which is exactly what brandOrderMeta() on the
                                    checkout screen already does. */}
                                {(product.effectiveOrderFlow ===
                                  "BRANDED_PACKAGING" ||
                                  product.orderFlowOverride ===
                                    "BRANDED_PACKAGING") && (
                                  <ProductBadge
                                    $color="var(--brand-secondary, #c9a227)"
                                    $text="var(--brand-on-secondary, #0f172a)"
                                    $besideDeal={onOffer}
                                  >
                                    Custom print
                                  </ProductBadge>
                                )}
                                {/* WAS `product.orderFlowOverride === "QUOTE"`,
                                    which is the SKU's own override and nothing
                                    else. A brand whose whole catalogue is sold
                                    by quote leaves its SKUs on INHERIT, so every
                                    product that most needed this badge was
                                    exactly the one that never got it.
                                    `effectiveOrderFlow`, which the helper reads,
                                    already folds in the vendor-wide and
                                    tier-wide posture as well. */}
                                {quoteOnly && (
                                  <ProductBadge
                                    $color="#6366f1"
                                    $text="#fff"
                                    $besideDeal={onOffer}
                                  >
                                    {/* While the buyer has a live request, what
                                        they need from this badge is their own
                                        progress, not the posture they already
                                        acted on. The price row still says
                                        "Price on request", so nothing is
                                        lost. */}
                                    {quoteProgress?.live
                                      ? quoteProgress.title
                                      : "Quote only"}
                                  </ProductBadge>
                                )}
                                {coverage.isOutOfRange && (
                                  <OutOfRangeBadge $besideDeal={onOffer}>
                                    Outside delivery area
                                  </OutOfRangeBadge>
                                )}
                              </ProductImageWrap>
                              <ProductDetails>
                                <ProductName>
                                  <CrawlLink
                                    to={productLink(product)}
                                    onNavigate={() => handleProductClick(product)}
                                  >
                                    {product.name}
                                  </CrawlLink>
                                </ProductName>
                                {product.skuCode && (
                                  <SkuLine>{product.skuCode}</SkuLine>
                                )}
                                {branchBlocked && (
                                  <BranchAvailabilityBadge
                                    compact
                                    product={product}
                                    branchName={hereName}
                                    locations={brandLocations}
                                    vendorName={displayTitle}
                                    onGetItFrom={getItFrom.request}
                                    accent={primary}
                                  />
                                )}
                                {product.description && (
                                  <CardDescriptionClamp>
                                    <ReactQuill
                                      value={product.description}
                                      readOnly={true}
                                      theme="bubble"
                                    />
                                  </CardDescriptionClamp>
                                )}
                                {/* Spec chips: only attributes the brand
                                explicitly opted to surface on the
                                card. Long-form attributes stay in the
                                detail modal. Includes both vendor-
                                fixed values and the first choice for
                                buyer-configurable variants (with a
                                tag indicating choice exists). */}
                                {(() => {
                                  const tplFields =
                                    selectedBrand?.productSpecTemplate
                                      ?.fields || [];
                                  if (!tplFields.length) return null;
                                  const cardFields = tplFields.filter(
                                    (f) => f.displayOnCard,
                                  );
                                  if (!cardFields.length) return null;
                                  const variants = Array.isArray(
                                    product.specOptions,
                                  )
                                    ? product.specOptions
                                    : [];
                                  const variantMap = new Map(
                                    variants.map((v) => [v.fieldKey, v]),
                                  );
                                  return (
                                    <SpecChipRow>
                                      {cardFields.map((f) => {
                                        const variant = variantMap.get(
                                          f.fieldKey,
                                        );
                                        if (variant) {
                                          const count =
                                            variant.choices?.length || 0;
                                          if (count === 0) return null;
                                          return (
                                            <SpecChip
                                              key={f.fieldKey}
                                              $variant
                                              title={`Choose ${f.label} on this product`}
                                            >
                                              {f.label}: {count} option
                                              {count !== 1 ? "s" : ""}
                                            </SpecChip>
                                          );
                                        }
                                        const value =
                                          product.specs?.[f.fieldKey];
                                        if (
                                          value === undefined ||
                                          value === null ||
                                          value === ""
                                        )
                                          return null;
                                        return (
                                          <SpecChip key={f.fieldKey}>
                                            <SpecChipLabel>
                                              {f.label}:
                                            </SpecChipLabel>{" "}
                                            {value}
                                            {f.unit ? ` ${f.unit}` : ""}
                                          </SpecChip>
                                        );
                                      })}
                                    </SpecChipRow>
                                  );
                                })()}
                                <PriceAndButtonContainer>
                                  <div>
                                    {/* Three states, not two. Quote-only is a
                                        commercial posture with a price behind a
                                        conversation; unpriced is a SKU nobody
                                        has costed yet. Both used to fall through
                                        to PriceCut, which printed the zero. */}
                                    {quoteOnly ? (
                                      <ProductPrice>
                                        {PRICE_ON_REQUEST_LABEL}
                                      </ProductPrice>
                                    ) : unpriced ? (
                                      <ProductPrice>
                                        {PRICE_UNAVAILABLE_LABEL}
                                      </ProductPrice>
                                    ) : (
                                      <PriceCut
                                        component={ProductPrice}
                                        price={shownPrice}
                                        listPrice={product.listPrice}
                                        salePrice={product.salePrice}
                                        format={fmtUgx}
                                        prefix={
                                          product.priceIsTeaser ? "From " : ""
                                        }
                                      />
                                    )}
                                    {product.priceIsTeaser && !quoteOnly && (
                                      <TierHint>
                                        Sign in for tier pricing
                                      </TierHint>
                                    )}
                                    {product.tierPrices?.wholesaler &&
                                      !quoteOnly &&
                                      !product.priceIsTeaser && (
                                        <TierHint>
                                          Tier pricing available
                                        </TierHint>
                                      )}
                                  </div>
                                  <Tooltip
                                    title={
                                      quoteOnly
                                        ? quoteProgress
                                          ? `${quoteProgress.title}. ${quoteProgress.action}`
                                          : "Request a quote"
                                        : unpriced
                                          ? "This item has no price yet, so it cannot be ordered"
                                          : branchBlocked
                                            ? addButtonLabel(product)
                                            : coverage.isOutOfRange
                                            ? "Outside delivery area, change your location to order"
                                            : isInCart
                                              ? `${cartQuantity} in cart - Click to modify`
                                              : "Add to Cart"
                                    }
                                  >
                                    <AddToCartButton
                                      $isInCart={isInCart}
                                      $quote={quoteOnly}
                                      /* An unpriced SKU has no posture behind
                                         it and no quote to ask for, and the
                                         server refuses the line (PRICE_UNSET),
                                         so an enabled button here could only
                                         ever produce a failed checkout. */
                                      disabled={unpriced || branchBlocked}
                                      style={{
                                        opacity:
                                          !quoteOnly && coverage.isOutOfRange
                                            ? 0.4
                                            : 1,
                                      }}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        if (quoteOnly) {
                                          // Modal handles channel selection; no need to gate on customQuote.
                                          openQuoteForProduct(product);
                                          return;
                                        }
                                        handleProductClick(product);
                                      }}
                                    >
                                      {quoteOnly ? (
                                        <RequestQuoteIcon
                                          style={{ fontSize: "1.1rem" }}
                                        />
                                      ) : isInCart ? (
                                        <>
                                          <CheckCircleIcon
                                            style={{
                                              fontSize: "1rem",
                                              filter:
                                                "drop-shadow(0 1px 2px rgba(0,0,0,0.25))",
                                            }}
                                          />
                                          <span
                                            style={{
                                              fontSize: "0.78rem",
                                              fontWeight: "800",
                                              lineHeight: 1,
                                              textShadow:
                                                "0 1px 2px rgba(0,0,0,0.2)",
                                            }}
                                          >
                                            {cartQuantity}
                                          </span>
                                        </>
                                      ) : (
                                        <AddShoppingCartIcon
                                          style={{ fontSize: "1.1rem" }}
                                        />
                                      )}
                                    </AddToCartButton>
                                  </Tooltip>
                                </PriceAndButtonContainer>
                              </ProductDetails>
                            </ProductCard>
                          );
                        })}
                    </ProductsGrid>
                  </Section>
                ))}
            </SectionsContainer>
            {searchQuery.trim() && visibleSections.length === 0 && (
              <NoProductsMessage>
                No products match &ldquo;{searchQuery}&rdquo;
              </NoProductsMessage>
            )}
          </>
        ) : !catalogueLocked ? (
          <NoProductsMessage>
            No products available for this brand at the moment.
          </NoProductsMessage>
        ) : null}

        {/* Brand-level reviews — anchored to the brand id from the URL. */}
        {id && (
          <Reviews
            subjectType="vendor_brand"
            subjectId={id}
            title={`Reviews for ${selectedBrand?.name || "this brand"}`}
          />
        )}

        {showQuoteModal && (
          <QuoteBackdrop
            onClick={() => !quoteSubmitting && setShowQuoteModal(false)}
          >
            <QuotePanel onClick={(e) => e.stopPropagation()}>
              <QuotePanelHeader>
                <QuotePanelTitle>Request a quote</QuotePanelTitle>
                <QuoteCloseBtn
                  type="button"
                  onClick={() => setShowQuoteModal(false)}
                >
                  ×
                </QuoteCloseBtn>
              </QuotePanelHeader>
              {quoteProduct && (
                <QuoteProductContext>
                  <QuoteProductName>{quoteProduct.name}</QuoteProductName>
                  {quoteProduct.skuCode && (
                    <QuoteProductSku>{quoteProduct.skuCode}</QuoteProductSku>
                  )}
                </QuoteProductContext>
              )}
              <form onSubmit={submitQuoteRequest}>
                {/* Platform-mediated reply route; direct vendor contacts stay private. */}
                <QuoteLabel>Communication and support</QuoteLabel>
                <ChannelGrid>
                  {availableChannels(selectedBrand).map((ch) => {
                    const meta = {
                      IN_PLATFORM: {
                        Icon: ChatIcon,
                        color: "#0a578b",
                        label: "On Lusuku",
                        sub: "Reply lands in My Quotes — secure & tracked",
                      },
                    }[ch];
                    if (!meta) return null;
                    const active = quoteForm.preferredChannel === ch;
                    const Icon = meta.Icon;
                    return (
                      <ChannelCard
                        key={ch}
                        type="button"
                        $active={active}
                        onClick={() =>
                          setQuoteForm((f) => ({ ...f, preferredChannel: ch }))
                        }
                      >
                        <ChannelIcon style={{ color: meta.color }}>
                          <Icon style={{ fontSize: 22 }} />
                        </ChannelIcon>
                        <ChannelLabel>{meta.label}</ChannelLabel>
                        <ChannelSub>{meta.sub}</ChannelSub>
                        {active && <ChannelCheck>✓</ChannelCheck>}
                      </ChannelCard>
                    );
                  })}
                </ChannelGrid>
                <ChannelFootnote>
                  The vendor is notified without their private contact details
                  being shared. Replies, support and closure stay securely
                  tracked in <strong>My Quotes</strong>.
                </ChannelFootnote>

                {/* Contact snapshot — buyer can override their account-default contact for this request. */}
                <QuoteFieldRow>
                  <QuoteFieldGroup>
                    <QuoteLabel>
                      Your name <QuoteRequired>*</QuoteRequired>
                    </QuoteLabel>
                    <FormField
                      placeholder="Full name"
                      value={quoteForm.contactName}
                      onChange={(e) =>
                        setQuoteForm((f) => ({
                          ...f,
                          contactName: e.target.value,
                        }))
                      }
                      required
                    />
                  </QuoteFieldGroup>
                  <QuoteFieldGroup>
                    <QuoteLabel>
                      Reach me on <QuoteRequired>*</QuoteRequired>
                    </QuoteLabel>
                    {quoteForm.preferredChannel === "EMAIL" ||
                    quoteForm.preferredChannel === "IN_PLATFORM" ? (
                      <FormField
                        type="email"
                        placeholder="you@example.com"
                        value={quoteForm.contactEmail}
                        onChange={(e) =>
                          setQuoteForm((f) => ({
                            ...f,
                            contactEmail: e.target.value,
                          }))
                        }
                        required
                      />
                    ) : (
                      <FormField
                        type="tel"
                        placeholder={`${DIAL_CODE} 7XX XXX XXX`}
                        value={quoteForm.contactPhone}
                        onChange={(e) =>
                          setQuoteForm((f) => ({
                            ...f,
                            contactPhone: e.target.value,
                          }))
                        }
                        required
                      />
                    )}
                  </QuoteFieldGroup>
                </QuoteFieldRow>

                <QuoteLabel>Subject</QuoteLabel>
                <FormField
                  placeholder="e.g. Quote for 500 units of Kraft mailer box M"
                  value={quoteForm.title}
                  onChange={(e) =>
                    setQuoteForm((f) => ({ ...f, title: e.target.value }))
                  }
                  required
                />
                <QuoteLabel>
                  What do you need? <QuoteOptional>(optional)</QuoteOptional>
                </QuoteLabel>
                <FormArea
                  placeholder="Describe your use case or any special requirements…"
                  rows={2}
                  value={quoteForm.message}
                  onChange={(e) =>
                    setQuoteForm((f) => ({ ...f, message: e.target.value }))
                  }
                />
                <QuoteFieldRow>
                  <QuoteFieldGroup>
                    <QuoteLabel>
                      Quantity needed <QuoteRequired>*</QuoteRequired>
                    </QuoteLabel>
                    <FormField
                      placeholder="e.g. 500 units"
                      value={quoteForm.quantity}
                      onChange={(e) =>
                        setQuoteForm((f) => ({
                          ...f,
                          quantity: e.target.value,
                        }))
                      }
                      required
                    />
                  </QuoteFieldGroup>
                  <QuoteFieldGroup>
                    <QuoteLabel>
                      Deadline <QuoteRequired>*</QuoteRequired>
                    </QuoteLabel>
                    <DeadlineDateInput
                      type="date"
                      value={quoteForm.deadline}
                      min={new Date().toISOString().slice(0, 10)}
                      onChange={(e) =>
                        setQuoteForm((f) => ({
                          ...f,
                          deadline: e.target.value,
                        }))
                      }
                      required
                    />
                  </QuoteFieldGroup>
                </QuoteFieldRow>
                <QuoteLabel>
                  Delivery location <QuoteRequired>*</QuoteRequired>
                </QuoteLabel>
                <QuoteCombobox onSelect={handleDeliverySelect}>
                  <QuoteAddressInputWrap>
                    <QuoteComboboxInput
                      value={deliveryAddressInput}
                      onChange={(e) => {
                        setDeliveryAddressInput(e.target.value);
                        setQuoteForm((f) => ({
                          ...f,
                          deliveryLocation: e.target.value,
                        }));
                        setDeliveryLatLng(null);
                        setDeliveryAddressError("");
                      }}
                      disabled={!placesReady}
                      placeholder={
                        placesReady
                          ? DELIVERY_LOCATION_EXAMPLE
                          : "Loading address search…"
                      }
                      autoComplete="off"
                    />
                    {deliveryAddressInput.length > 1 &&
                      !deliveryLatLng &&
                      deliverySuggestions.length === 0 && (
                        <QuoteAddressSpinner />
                      )}
                  </QuoteAddressInputWrap>
                  {deliverySuggestions.length > 0 && (
                    <QuoteComboboxPopover>
                      <ComboboxList>
                        {deliverySuggestions.map((s) => (
                          <ComboboxOption
                            key={s.place_id}
                            value={s.description}
                          />
                        ))}
                      </ComboboxList>
                    </QuoteComboboxPopover>
                  )}
                </QuoteCombobox>
                {deliveryLatLng && (
                  <QuoteGeoConfirm>✓ Location confirmed</QuoteGeoConfirm>
                )}
                {deliveryAddressError && (
                  <div
                    role="alert"
                    style={{
                      color: "#b3362a",
                      fontSize: "12px",
                      marginTop: "4px",
                    }}
                  >
                    {deliveryAddressError}
                  </div>
                )}

                {(() => {
                  const cqConfig = selectedBrand?.flows?.customQuoteConfig;
                  if (
                    !cqConfig ||
                    !cqConfig.fields ||
                    cqConfig.fields.length === 0
                  )
                    return null;
                  return (
                    <>
                      {cqConfig.introText && (
                        <QuoteCustomIntro>
                          {cqConfig.introText}
                        </QuoteCustomIntro>
                      )}
                      {cqConfig.fields.map((field) => (
                        <div key={field.fieldKey}>
                          <QuoteLabel>
                            {field.label}
                            {field.required ? (
                              <QuoteRequired> *</QuoteRequired>
                            ) : (
                              <QuoteOptional> (optional)</QuoteOptional>
                            )}
                          </QuoteLabel>
                          {field.helpText && (
                            <QuoteHelpText>{field.helpText}</QuoteHelpText>
                          )}
                          {field.fieldType === "textarea" ? (
                            <FormArea
                              rows={3}
                              placeholder={field.placeholder || ""}
                              value={customFieldValues[field.fieldKey] || ""}
                              onChange={(e) =>
                                setCustomFieldValues((v) => ({
                                  ...v,
                                  [field.fieldKey]: e.target.value,
                                }))
                              }
                              required={field.required}
                            />
                          ) : field.fieldType === "select" ? (
                            <QuoteSelect
                              value={customFieldValues[field.fieldKey] || ""}
                              onChange={(e) =>
                                setCustomFieldValues((v) => ({
                                  ...v,
                                  [field.fieldKey]: e.target.value,
                                }))
                              }
                              required={field.required}
                            >
                              <option value="">— Select —</option>
                              {(field.options || []).map((opt) => (
                                <option key={opt} value={opt}>
                                  {opt}
                                </option>
                              ))}
                            </QuoteSelect>
                          ) : (
                            <FormField
                              type={
                                field.fieldType === "number"
                                  ? "number"
                                  : field.fieldType === "date"
                                    ? "date"
                                    : "text"
                              }
                              placeholder={field.placeholder || ""}
                              value={customFieldValues[field.fieldKey] || ""}
                              onChange={(e) =>
                                setCustomFieldValues((v) => ({
                                  ...v,
                                  [field.fieldKey]: e.target.value,
                                }))
                              }
                              required={field.required}
                            />
                          )}
                        </div>
                      ))}
                    </>
                  );
                })()}

                <QuoteActions>
                  <button
                    type="button"
                    onClick={() => setShowQuoteModal(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={(() => {
                      if (quoteSubmitting) return true;
                      if (
                        !quoteForm.title.trim() ||
                        !quoteForm.quantity.trim() ||
                        !quoteForm.deliveryLocation.trim() ||
                        !quoteForm.deadline.trim()
                      )
                        return true;
                      const cqFields =
                        selectedBrand?.flows?.customQuoteConfig?.fields || [];
                      const missingCustom = cqFields
                        .filter((f) => f.required)
                        .some(
                          (f) =>
                            !customFieldValues[f.fieldKey]?.toString().trim(),
                        );
                      return missingCustom;
                    })()}
                  >
                    {quoteSubmitting ? "Sending…" : "Send quote request"}
                  </button>
                </QuoteActions>
              </form>
            </QuotePanel>
          </QuoteBackdrop>
        )}

        {/* product detail is now BrandProductPage */}

        <FloatingCartButton
          $primary={secondary}
          $text={onSecondary}
          style={{ display: catalogueLocked ? "none" : "flex" }}
          onClick={handleCartClick}
        >
          <Badge
            badgeContent={cartCount > 0 ? cartCount : null}
            sx={{
              "& .MuiBadge-badge": {
                backgroundColor: primary,
                color: onPrimary,
              },
            }}
          >
            <ShoppingCartIcon />
          </Badge>
        </FloatingCartButton>

        {/* "Changes at <branch>", after the cart moved to another branch. */}
        <BranchCartReviewDialog {...branchDialogProps} />
        {/* "Get it from <branch>" with items from another branch in the cart. */}
        <OutletSwitchDialog
          open={Boolean(getItFrom.pending)}
          fromLabel={
            cartOutlet
              ? findOutlet(selectedBrand, cartOutlet)?.name || displayTitle
              : locationName({ isOutlet: false }, displayTitle)
          }
          toLabel={locationName(getItFrom.pending, displayTitle)}
          onCancel={getItFrom.cancel}
          onConfirm={getItFrom.confirm}
        />

        {/* Cart Modal */}
        <Modal
          open={showCart}
          onClose={() => setShowCart(false)}
          closeAfterTransition
          BackdropComponent={Backdrop}
          BackdropProps={{
            timeout: 500,
          }}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Fade in={showCart}>
            <div style={{ outline: "none" }}>
              <BrandCart
                onClose={() => setShowCart(false)}
                brandId={id}
                brand={selectedBrand}
              />
            </div>
          </Fade>
        </Modal>
        <VendorWhatsAppFloat
          contactUrl={withOutletQuery(
            `${process.env.REACT_APP_SERVER_URL || ""}/api/brands/${id}/contact/whatsapp`,
            outletId,
          )}
          vendorName={
            selectedOutlet
              ? `${displayTitle} ${selectedOutlet.name}`
              : selectedBrand?.tradingName || selectedBrand?.name
          }
          vendorType="brand"
        />
        {showLocationPrompt && (
          <LocationPrompt open onClose={() => setShowLocationPrompt(false)} />
        )}
      </Container>
    </Layout>
  );
};

export default BrandShopPage;

// Styled Components
const Container = styled.div`
  max-width: 1400px;
  margin: 2rem auto;
  padding: 0 20px;
  min-height: 60vh;
  position: relative;
`;

/**
 * Positioning context for the Back button. On desktop it sits in the whitespace
 * above the carousel so brand artwork is never obscured. On narrow screens it
 * becomes an in-flow control above the artwork because the available top margin
 * is smaller.
 */
const HeroWrap = styled.div`
  position: relative;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
`;

const HeroShell = styled.section`
  position: relative;
  width: 100%;
  max-width: 1200px;
  border-radius: 16px;
  overflow: hidden;
  margin: 0 auto;
  background: var(--brand-primary, #1a1a2e);
  aspect-ratio: 15 / 4;

  @media (max-width: 768px) {
    border-radius: 12px;
  }

  @media (max-width: 480px) {
    border-radius: 10px;
  }
`;

const HeroTrack = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  transform: translate3d(${(p) => `-${p.$index * 100}%`}, 0, 0);
  transition: transform 500ms cubic-bezier(0.22, 1, 0.36, 1);
  will-change: transform;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const HeroSlide = styled.div`
  position: relative;
  flex: 0 0 100%;
  width: 100%;
  height: 100%;
  overflow: hidden;
  background: var(--brand-primary, #1a1a2e);
`;

/* Featured artwork is authored at 16:9 for the landing cards. The wide shop
   hero must not cut its headlines or logos, so the complete artwork is shown
   over a soft, colour-matched backdrop instead of being aggressively cropped. */
const HeroArtworkBackdrop = styled.div`
  position: absolute;
  inset: -24px;
  background-position: center;
  background-size: cover;
  filter: blur(20px) saturate(0.9) brightness(0.72);
  opacity: 0.82;
  transform: scale(1.08);

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    background: rgba(8, 15, 18, 0.18);
  }
`;

const HeroBannerImg = styled.img`
  position: relative;
  z-index: 1;
  width: 100%;
  height: 100%;
  /* Dedicated shop banners are exact 15:4 assets. Legacy card-image
     fallbacks use contain so they are not cropped. */
  object-fit: ${(p) => (p.$fit ? "cover" : "contain")};
  object-position: center;
  display: block;
`;

const HeroArrow = styled.button`
  position: absolute;
  z-index: 4;
  top: 50%;
  ${(p) => (p.$side === "left" ? "left: 14px;" : "right: 14px;")}
  width: 38px;
  height: 38px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transform: translateY(-50%);
  border: 1px solid rgba(255, 255, 255, 0.34);
  border-radius: 999px;
  background: rgba(10, 18, 21, 0.52);
  color: #fff;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.2);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  font:
    400 2rem/1 Arial,
    sans-serif;
  cursor: pointer;
  transition:
    background 160ms ease,
    transform 160ms ease;

  &:hover {
    background: rgba(10, 18, 21, 0.75);
    transform: translateY(-50%) scale(1.05);
  }

  &:focus-visible {
    outline: 3px solid #fff;
    outline-offset: 2px;
  }

  /* On a phone the two buttons sit over the middle of the artwork and hide
     it (a 38px disc each side of a 320px banner is a fifth of the picture).
     Swipe (handleHeroTouchStart/End) and the dots below already move the
     slides there, so the arrows are a desktop and keyboard affordance. */
  @media (max-width: 640px), (hover: none) and (pointer: coarse) {
    display: none;
  }
`;

const HeroDots = styled.div`
  position: absolute;
  z-index: 4;
  left: 50%;
  bottom: 12px;
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 6px 9px;
  transform: translateX(-50%);
  border-radius: 999px;
  background: rgba(10, 18, 21, 0.45);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);

  @media (max-width: 640px) {
    bottom: 7px;
    gap: 5px;
    padding: 4px 7px;
  }
`;

const HeroDot = styled.button`
  width: ${(p) => (p.$active ? "22px" : "7px")};
  height: 7px;
  padding: 0;
  border: 0;
  border-radius: 999px;
  background: ${(p) => (p.$active ? "#fff" : "rgba(255, 255, 255, 0.58)")};
  box-shadow: ${(p) => (p.$active ? "0 0 0 1px rgba(0, 0, 0, 0.08)" : "none")};
  cursor: pointer;
  transition:
    width 180ms ease,
    background 180ms ease;

  &:focus-visible {
    outline: 2px solid #fff;
    outline-offset: 2px;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const HeroBannerFallback = styled.div`
  width: 100%;
  height: 100%;
  background: linear-gradient(
    135deg,
    ${(p) => p.$primary || "#324d54"} 0%,
    #1a2c32 100%
  );
`;

const BrandIdentityCard = styled.div`
  display: flex;
  width: 100%;
  max-width: 1200px;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1.25rem;
  background: #fff;
  border-radius: 0 0 16px 16px;
  padding: 1.25rem 1.75rem 1.5rem;
  margin: 0 auto 1.25rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid #e8edf2;
  border-top: none;

  @media (max-width: 640px) {
    padding: 1rem 1rem 1.25rem;
    flex-direction: column;
  }
`;

const BrandIdentityLeft = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  flex: 1 1 auto;
  min-width: 0;
`;

const BrandLogoImg = styled.img`
  width: 72px;
  height: 72px;
  object-fit: contain;
  border-radius: 12px;
  background: #f8fafc;
  padding: 6px;
  border: 1.5px solid #e2e8f0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  flex-shrink: 0;

  @media (max-width: 480px) {
    width: 56px;
    height: 56px;
  }
`;

const BrandLogoInitials = styled.div`
  width: 72px;
  height: 72px;
  border-radius: 12px;
  background: ${(p) => p.$primary || "#324d54"};
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 800;
  font-size: 1.35rem;
  flex-shrink: 0;
`;

const BrandIdentityText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const BrandIdentityTitle = styled.h1`
  font-size: 1.45rem;
  font-weight: 800;
  color: #0f172a;
  margin: 0;
  line-height: 1.2;
  letter-spacing: -0.02em;

  @media (max-width: 480px) {
    font-size: 1.2rem;
  }
`;

const BrandIdentityLegal = styled.div`
  font-size: 0.82rem;
  color: #64748b;
  margin-top: 1px;
`;

const BrandIdentityMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.82rem;
  color: #475569;
  margin-top: 3px;

  svg {
    flex-shrink: 0;
    color: #64748b;
  }
`;


const BrandIdentityTagline = styled.p`
  font-size: 0.95rem;
  color: #1e293b;
  margin: 6px 0 0;
  font-weight: 600;
  line-height: 1.4;
`;

/**
 * The brand's speciality. Solid brand colour, uppercase, shrink-wrapped —
 * `align-self` matters because BrandIdentityText is a flex COLUMN, so without
 * it the pill stretches the full width of the card.
 */
const SpecialityPill = styled.span`
  display: inline-flex;
  align-items: center;
  align-self: flex-start;
  margin-top: 8px;
  padding: 6px 14px;
  border-radius: 999px;
  background: ${(p) => p.$bg || "#324D54"};
  color: ${(p) => p.$fg || "#ffffff"};
  font-size: 0.76rem;
  font-weight: 800;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  line-height: 1.2;
  white-space: nowrap;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.18);

  @media (max-width: 480px) {
    font-size: 0.72rem;
    padding: 5px 12px;
    letter-spacing: 0.05em;
  }
`;

const BrandIdentityRight = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 10px;
  flex-shrink: 0;

  @media (max-width: 640px) {
    align-items: flex-start;
  }
`;

const TrustStrip = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  justify-content: flex-end;

  @media (max-width: 640px) {
    justify-content: flex-start;
  }
`;

const TrustChip = styled.span`
  background: ${(p) => (p.$accent ? p.$accent + "18" : "#f1f5f9")};
  color: ${(p) => p.$accent || "#334155"};
  padding: 5px 11px;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 700;
  border: 1px solid ${(p) => (p.$accent ? p.$accent + "33" : "#e2e8f0")};
  white-space: nowrap;
`;

const CtaRow = styled.div`
  display: flex;
  gap: 10px;
`;

const HeroButton = styled.button`
  padding: 9px 20px;
  border-radius: 10px;
  border: none;
  font-weight: 700;
  font-size: 0.88rem;
  cursor: pointer;
  background: var(--brand-secondary, #c9a227);
  color: var(--brand-on-secondary, #0f172a);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.14);
  transition: opacity 0.18s;
  &:hover {
    opacity: 0.88;
  }
`;

/* Share sits beside Request quote (or alone) in the brand's own colours:
   outlined so the quote CTA stays the loud one, but never hidden — a brand
   sharing its storefront is the cheapest acquisition this page can do. */
const ShareStoreButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border-radius: 10px;
  font-weight: 700;
  font-size: 0.88rem;
  cursor: pointer;
  background: #fff;
  color: var(--brand-primary, #0f172a);
  border: 1.5px solid var(--brand-primary, #0f172a);
  transition: background 0.18s, color 0.18s;
  &:hover {
    background: var(--brand-primary, #0f172a);
    color: var(--brand-on-primary, #fff);
  }
  &:focus-visible {
    outline: 2px solid var(--brand-secondary, #c9a227);
    outline-offset: 2px;
  }
`;

const BrandDescriptionBar = styled.div`
  background: #f8fafc;
  border-left: 4px solid var(--brand-secondary, #c9a227);
  border-radius: 0 10px 10px 0;
  padding: 0.85rem 1.25rem;
  margin-bottom: 1.5rem;
  font-size: 0.93rem;
  color: #334155;
  line-height: 1.65;
  p {
    margin: 0 0 0.4em;
  }
  p:last-child {
    margin: 0;
  }
  strong {
    color: #0f172a;
  }
`;

const LockCard = styled.div`
  text-align: center;
  padding: 3rem 2rem;
  border-radius: 20px;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border: 1px solid #e2e8f0;
  margin-bottom: 2rem;
  a {
    text-decoration: none;
  }
`;

const LockIcon = styled.div`
  font-size: 2.5rem;
  margin-bottom: 0.75rem;
`;

const LockTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 700;
  color: #1a1a2e;
  margin: 0 0 0.5rem;
`;

const LockText = styled.p`
  color: #64748b;
  font-size: 0.95rem;
  max-width: 520px;
  margin: 0 auto 1.5rem;
  line-height: 1.6;
`;

const BrandPrimaryButton = styled.button`
  background: var(--brand-secondary, #c9a227);
  color: var(--brand-on-secondary, #0f172a);
  border: none;
  padding: 12px 28px;
  border-radius: 10px;
  font-size: 0.95rem;
  font-weight: 700;
  cursor: pointer;
  transition: opacity 0.2s;
  &:hover {
    opacity: 0.88;
  }
`;

const ChannelGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 8px;
  margin: 4px 0 6px 0;
`;

const ChannelCard = styled.button`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
  gap: 2px;
  padding: 12px 12px 10px 12px;
  border-radius: 10px;
  border: ${(p) => (p.$active ? "2px solid #0a578b" : "1px solid #e2e8f0")};
  background: ${(p) => (p.$active ? "#eff6ff" : "#fff")};
  cursor: pointer;
  transition: all 0.15s ease;
  min-height: 70px;
  &:hover {
    border-color: #0a578b;
  }
`;

const ChannelIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 2px;
  line-height: 1;
`;

const ChannelLabel = styled.span`
  font-size: 13px;
  font-weight: 700;
  color: #0f172a;
`;

const ChannelSub = styled.span`
  font-size: 11px;
  color: #64748b;
  line-height: 1.3;
  word-break: break-word;
`;

const ChannelCheck = styled.span`
  position: absolute;
  top: 6px;
  right: 8px;
  font-size: 12px;
  color: #0a578b;
  font-weight: 700;
`;

const ChannelFootnote = styled.p`
  font-size: 11px;
  color: #64748b;
  line-height: 1.5;
  margin: 4px 0 12px 0;
  padding: 8px 10px;
  background: #f8fafc;
  border-left: 3px solid #0a578b;
  border-radius: 0 6px 6px 0;
`;

const QuoteBackdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 2000;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  overflow: hidden;

  @media (max-width: 480px) {
    padding: 8px;
  }
`;

const QuotePanel = styled.div`
  background: #fff;
  border-radius: 12px;
  padding: 1.5rem;
  max-width: 560px;
  width: 100%;
  max-height: calc(100vh - 32px);
  max-height: calc(100dvh - 32px);
  overflow-x: hidden;
  overflow-y: auto;
  overscroll-behavior-y: contain;
  -webkit-overflow-scrolling: touch;
  touch-action: pan-y;
  box-sizing: border-box;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.2);

  @media (max-width: 480px) {
    max-height: calc(100vh - 16px);
    max-height: calc(100dvh - 16px);
    padding: 1rem;
    border-radius: 10px;
  }
`;

const FormField = styled.input`
  width: 100%;
  box-sizing: border-box;
  margin-bottom: 10px;
  padding: 10px;
  border-radius: 8px;
  border: 1px solid #cbd5e1;
  font-size: 0.95rem;
`;

const FormArea = styled.textarea`
  width: 100%;
  box-sizing: border-box;
  margin-bottom: 10px;
  padding: 10px;
  border-radius: 8px;
  border: 1px solid #cbd5e1;
  font-size: 0.95rem;
  resize: vertical;
`;

const QuoteActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 8px;
  button {
    padding: 8px 16px;
    border-radius: 8px;
    border: none;
    cursor: pointer;
    font-weight: 600;
  }
  button[type="submit"] {
    background: var(--brand-secondary, #c9a227);
    color: var(--brand-on-secondary, #0f172a);
  }
  button[type="submit"]:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const QuotePanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
`;

const QuotePanelTitle = styled.h3`
  margin: 0;
  font-size: 1.05rem;
  font-weight: 700;
  color: #1a1a2e;
`;

const QuoteCloseBtn = styled.button`
  background: none;
  border: none;
  font-size: 1.4rem;
  line-height: 1;
  color: #64748b;
  cursor: pointer;
  padding: 0 4px;
  &:hover {
    color: #1a1a2e;
  }
`;

const QuoteProductContext = styled.div`
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 10px 12px;
  margin-bottom: 14px;
`;

const QuoteProductName = styled.div`
  font-size: 0.92rem;
  font-weight: 600;
  color: #1a1a2e;
`;

const QuoteProductSku = styled.div`
  font-size: 0.75rem;
  color: #64748b;
  margin-top: 2px;
  font-family: monospace;
`;

const QuoteLabel = styled.label`
  display: block;
  font-size: 0.78rem;
  font-weight: 600;
  color: #475569;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 4px;
`;

const QuoteOptional = styled.span`
  font-weight: 400;
  text-transform: none;
  letter-spacing: 0;
  color: #94a3b8;
`;

const QuoteRequired = styled.span`
  color: #ef4444;
  font-weight: 700;
  margin-left: 1px;
`;

const QuoteFieldRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const QuoteFieldGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const QuoteCombobox = styled(Combobox)`
  position: relative;
  width: 100%;
  margin-bottom: 6px;
`;

const QuoteComboboxInput = styled(ComboboxInput)`
  width: 100%;
  padding: 10px 12px;
  border: 1.5px solid #cbd5e1;
  border-radius: 8px;
  font-size: 0.95rem;
  outline: none;
  box-sizing: border-box;
  font-family: inherit;
  transition: border-color 0.18s;
  &:focus {
    border-color: var(--brand-primary, #324d54);
    background: #fff;
  }
  &::placeholder {
    color: #94a3b8;
  }
`;

const QuoteComboboxPopover = styled(ComboboxPopover)`
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  background: #fff;
  z-index: 9999;
  [data-reach-combobox-option] {
    padding: 8px 12px;
    font-size: 0.9rem;
    cursor: pointer;
    &:hover,
    &[aria-selected="true"] {
      background: #f1f5f9;
    }
  }
`;

const QuoteGeoConfirm = styled.div`
  font-size: 0.78rem;
  color: #16a34a;
  font-weight: 600;
  margin-bottom: 10px;
  margin-top: -2px;
`;

const DeadlineDateInput = styled.input`
  width: 100%;
  padding: 10px 12px;
  border: 1.5px solid #cbd5e1;
  border-radius: 8px;
  font-size: 0.95rem;
  outline: none;
  box-sizing: border-box;
  font-family: inherit;
  background: #f8fafc;
  color: #1a1a2e;
  cursor: pointer;
  transition: border-color 0.18s;
  &:focus {
    border-color: var(--brand-primary, #324d54);
    background: #fff;
  }
`;

const QuoteAddressInputWrap = styled.div`
  position: relative;
  width: 100%;
`;

const quoteAddrSpin = keyframes`
  to { transform: rotate(360deg); }
`;

const QuoteAddressSpinner = styled.span`
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  width: 16px;
  height: 16px;
  border: 2px solid #e2e8f0;
  border-top-color: var(--brand-primary, #324d54);
  border-radius: 50%;
  animation: ${quoteAddrSpin} 0.7s linear infinite;
  pointer-events: none;
`;

const QuoteCustomIntro = styled.p`
  font-size: 0.87rem;
  color: #475569;
  background: #f1f5f9;
  border-left: 3px solid var(--brand-primary, #324d54);
  padding: 8px 12px;
  border-radius: 0 6px 6px 0;
  margin: 14px 0 10px;
  line-height: 1.5;
`;

const QuoteHelpText = styled.p`
  font-size: 0.78rem;
  color: #94a3b8;
  margin: -6px 0 6px;
  line-height: 1.4;
`;

const QuoteSelect = styled.select`
  width: 100%;
  padding: 10px 12px;
  border: 1.5px solid #cbd5e1;
  border-radius: 8px;
  font-size: 0.95rem;
  background: #f8fafc;
  color: #1a1a2e;
  outline: none;
  margin-bottom: 10px;
  font-family: inherit;
  cursor: pointer;
  transition: border-color 0.18s;
  &:focus {
    border-color: var(--brand-primary, #324d54);
    background: #fff;
  }
`;

// const Header styled component removed (unused)

const BackButton = styled.button`
  position: absolute;
  top: -22px;
  left: 14px;
  z-index: 10;
  background: rgba(0, 0, 0, 0.42);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.22);
  padding: 7px 16px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.88rem;
  font-weight: 600;
  transition: background 0.2s;

  &:hover {
    background: rgba(0, 0, 0, 0.62);
  }

  /* Narrow layouts have less whitespace above the carousel, so the control
     joins normal flow and reserves its own space. */
  @media (max-width: 768px) {
    position: static;
    display: inline-flex;
    align-items: center;
    margin-bottom: 10px;
    background: #eef2f3;
    color: #33474d;
    border: 1px solid #dbe3e5;
    backdrop-filter: none;
    -webkit-backdrop-filter: none;

    &:hover {
      background: #e2e9ea;
    }
  }
`;

const SectionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3rem;
`;

const Section = styled.div`
  margin-bottom: 3rem;
`;

const SectionTitle = styled.h2`
  color: var(--brand-primary, #324d54);
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
  padding-bottom: 0.6rem;
  border-bottom: 3px solid var(--brand-secondary, #c9a227);
  display: inline-block;
`;

const SectionDescription = styled.p`
  color: #666;
  font-size: 1rem;
  margin-bottom: 1.5rem;
`;

const ProductsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 1.5rem;
  }
`;

const ProductCard = styled.div`
  background: white;
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.07);
  cursor: pointer;
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease;
  border: 1px solid #f1f5f9;
  display: flex;
  flex-direction: column;
  height: 100%;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 28px rgba(0, 0, 0, 0.13);
    border-color: var(--brand-secondary, #c9a227);
  }
`;

const ProductImageWrap = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 1 / 1;
  overflow: hidden;
  background: #f8fafc;
`;

const ProductBadge = styled.span`
  position: absolute;
  top: 8px;
  /* The deal stub (48 wide) owns the top-left corner; chips step right. */
  left: ${(p) => (p.$besideDeal ? "56px" : "8px")};
  background: ${(p) => p.$color || "#c9a227"};
  color: ${(p) => p.$text || "#1a1a1a"};
  font-size: 0.65rem;
  font-weight: 700;
  padding: 3px 8px;
  border-radius: 999px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const OutOfRangeBadge = styled.div`
  position: absolute;
  top: 8px;
  left: ${(p) => (p.$besideDeal ? "56px" : "8px")};
  background: rgba(138, 43, 6, 0.92);
  color: #fff;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.02em;
  padding: 4px 8px;
  border-radius: 6px;
  z-index: 2;
  pointer-events: none;
`;

const ProductImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  padding: 14px;
  box-sizing: border-box;
  display: block;
  transition: transform 0.35s ease;
  ${ProductCard}:hover & {
    transform: scale(1.04);
  }
`;

const ProductDetails = styled.div`
  padding: 0.7rem 0.85rem 0.75rem;
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
`;

/* Title clamps to 2 lines but does NOT reserve the second line: cards are
   already height-matched by the grid (ProductCard is height:100%) and the
   price row is bottom-pinned with margin-top:auto, so a reserved second
   line only added dead space under single-line names. */
const ProductName = styled.h3`
  color: #1a1a2e;
  font-size: 1rem;
  margin: 0 0 2px;
  font-weight: 700;
  line-height: 1.3;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const SkuLine = styled.div`
  font-size: 0.72rem;
  color: #94a3b8;
  letter-spacing: 0.04em;
  line-height: 1.3;
  margin-bottom: 3px;
  font-family: monospace;
`;

/* Card-specific clamp: truncate rich description to a few lines so all
   cards share the same height regardless of how much copy the vendor
   pasted. The full description still renders in the detail modal. */
const CardDescriptionClamp = styled.div`
  position: relative;
  margin-bottom: 0.45rem;
  font-size: 0.85rem;
  color: #64748b;
  line-height: 1.4;
  max-height: calc(1.4em * 2);
  overflow: hidden;

  .ql-editor {
    padding: 0;
    line-height: 1.4;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  /* No bottom fade: it washed out the last line on every card, truncated
     or not. -webkit-line-clamp already appends an ellipsis when the copy
     is longer than the two lines we show. */
`;

const PriceAndButtonContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: auto;
  padding-top: 0.4rem;
`;

/* ── Spec chips on product cards ────────────────────────────────────────
   Compact attribute pills shown beneath the description for fields the
   brand opted to surface on the card (displayOnCard=true). Distinct
   styling for vendor-fixed values vs. buyer-configurable variants
   (`$variant`) so shoppers can see at a glance which attributes will
   prompt a choice in the detail modal. */
const SpecChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  margin: 0 0 0.45rem;
`;

const SpecChip = styled.span`
  font-size: 0.7rem;
  background: ${(p) => (p.$variant ? "rgba(99,102,241,0.08)" : "#f1f5f9")};
  color: ${(p) => (p.$variant ? "#4338ca" : "#334155")};
  border: 1px solid ${(p) => (p.$variant ? "#c7d2fe" : "#e2e8f0")};
  padding: 3px 8px;
  border-radius: 999px;
  line-height: 1.3;
  font-weight: 500;
  white-space: nowrap;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const SpecChipLabel = styled.span`
  font-weight: 600;
  color: #64748b;
`;

const ProductPrice = styled.span`
  font-size: 1.05rem;
  font-weight: 800;
  line-height: 1.2;
  color: var(--brand-primary, #324d54);
  display: block;
`;

const TierHint = styled.span`
  font-size: 0.7rem;
  line-height: 1.25;
  color: var(--brand-secondary, #c9a227);
  font-weight: 600;
  display: block;
`;

const AddToCartButton = styled(IconButton)`
  background: ${(props) =>
    props.$isInCart ? "#22c55e" : "var(--brand-secondary, #c9a227)"} !important;
  color: ${(props) =>
    props.$isInCart ? "#fff" : "var(--brand-on-secondary, #0f172a)"} !important;
  width: ${(props) => (props.$isInCart ? "auto" : "36px")} !important;
  min-width: ${(props) => (props.$isInCart ? "60px" : "36px")} !important;
  height: 36px !important;
  border-radius: ${(props) => (props.$isInCart ? "18px" : "50%")} !important;
  padding: ${(props) => (props.$isInCart ? "0 10px" : "0")} !important;
  gap: 4px;
  box-shadow: ${(props) =>
    props.$isInCart
      ? "0 3px 10px rgba(34,197,94,0.45), 0 1px 3px rgba(0,0,0,0.15)"
      : props.$quote
        ? "0 3px 10px rgba(0,0,0,0.2), 0 1px 3px rgba(0,0,0,0.12)"
        : "0 2px 6px rgba(0,0,0,0.18)"} !important;
  transition:
    box-shadow 0.2s,
    background 0.2s,
    transform 0.15s !important;

  &:hover {
    background: ${(props) =>
      props.$isInCart
        ? "#16a34a"
        : "var(--brand-secondary-hover, #ad8b22)"} !important;
    color: ${(props) =>
      props.$isInCart
        ? "#fff"
        : "var(--brand-on-secondary, #0f172a)"} !important;
    transform: translateY(-1px);
    box-shadow: ${(props) =>
      props.$isInCart
        ? "0 5px 14px rgba(34,197,94,0.5), 0 2px 4px rgba(0,0,0,0.15)"
        : "0 4px 10px rgba(0,0,0,0.22)"} !important;
  }

  &:disabled {
    background: #cbd5e1 !important;
    opacity: 0.6;
    box-shadow: none !important;
  }
`;

const FloatingCartButton = styled(IconButton)`
  position: fixed !important;
  bottom: 96px;
  right: 30px;
  background: ${(p) => p.$primary || "#324d54"} !important;
  color: ${(p) => p.$text || "#fff"} !important;
  width: 60px !important;
  height: 60px !important;
  box-shadow:
    0 4px 6px rgba(0, 0, 0, 0.14),
    0 10px 20px rgba(0, 0, 0, 0.18),
    0 20px 40px rgba(0, 0, 0, 0.1),
    0 1px 3px rgba(0, 0, 0, 0.08) !important;
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease,
    opacity 0.2s ease !important;
  z-index: 1400;

  &:hover {
    opacity: 0.92;
    transform: translateY(-3px);
    box-shadow:
      0 6px 10px rgba(0, 0, 0, 0.16),
      0 18px 34px rgba(0, 0, 0, 0.22),
      0 30px 54px rgba(0, 0, 0, 0.12),
      0 1px 3px rgba(0, 0, 0, 0.1) !important;
  }

  @media (max-width: 640px) {
    bottom: 76px;
  }
`;

const NoProductsMessage = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: #666;
  font-size: 1.2rem;
`;

const ErrorMessage = styled.div`
  text-align: center;
  color: #d32f2f;
  font-size: 1.2rem;
  padding: 3rem;
`;

const RetryButton = styled.button`
  margin-top: 1rem;
  padding: 0.6rem 1.4rem;
  border: 0;
  border-radius: 8px;
  background: #083860;
  color: #ffffff;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
`;
